import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { useUserAuth } from "../../contexts/authContext";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GetTimezone } from "../../util";

export default function HimsAnalysisCreator(props) {
    const [systems, setSystems] = useState([]);
    const [selectedSystem, setSelectedSystem] = useState("");
    const [startTime, setStartTime] = useState(dayjs().subtract(30, "days"));
    const [endTime, setEndTime] = useState(dayjs());

    const [selectedSystemError, setSelectedSystemError] = useState("");
    const [startTimeError, setStartTimeError] = useState("");
    const [endTimeError, setEndTimeError] = useState("");

    const { user, handleFailedFetch } = useUserAuth();

    useEffect(() => {
        const fetchData = async () => {
            const requestOptions = {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            };
            await fetch("/api/hms/system/get", requestOptions)
                .then((response) => (response.ok ? response.json() : Promise.reject(response)))
                .then((data) => setSystems(data))
                .catch((err) => handleFailedFetch(err));
        };

        fetchData();
    }, []);

    const handleSubmit = () => {
        setSelectedSystemError("");
        setStartTimeError("");
        setEndTimeError("");

        let validSubmission = true;
        if (!selectedSystem) {
            setSelectedSystemError("Please select a valid system.");
            validSubmission = false;
        }
        if (!startTime || !startTime.isValid()) {
            setStartTimeError("Please enter a valid date.");
            validSubmission = false;
        }
        if (!endTime || !endTime.isValid() || endTime.isBefore(startTime) || endTime.diff(startTime, "days") > 60) {
            setEndTimeError("Please enter a date that is after and within 60 days of the start date.");
            validSubmission = false;
        }
        if (endTime && endTime.isAfter(dayjs())) {
            setEndTimeError("Please enter a date that is not in the future.");
            validSubmission = false;
        }

        if (!validSubmission) {
            return;
        }

        const uuid = uuidv4();
        const body = {
            uuid,
            report_type: "HIMS",
            request_uuid: uuid,
            system_uuid: selectedSystem,
            time_start: startTime.toISOString(),
            time_end: endTime.toISOString(),
            user_id: user.id
        };

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        };
        fetch("/api/analysis/hims/submit", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                props.setCreatingReportType(null);
                props.getReports();
            })
            .catch((err) => handleFailedFetch(err));
    };

    const handleClose = () => {
        props.setCreatingReportType(null);
    };

    return (
        <Dialog onClose={handleClose} open={true} maxWidth="lg" PaperProps={{ sx: { minWidth: { sm: "500px" }, width: { xs: "100%", sm: "auto" } } }}>
            <DialogTitle>HIMS Analysis Request</DialogTitle>
            <DialogContent sx={{ overflow: "visible" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3, "& > *": { margin: "0 !important" }, pt: "15px" }}>
                    <TextField
                        select
                        fullWidth
                        label="System"
                        value={selectedSystem}
                        onChange={(e) => setSelectedSystem(e.target.value)}
                        error={selectedSystemError.length > 0}
                        helperText={selectedSystemError}
                    >
                        {systems.map((system) => (
                            <MenuItem key={system.system_uuid} value={system.system_uuid}>
                                {system.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label={`Start Time (${GetTimezone()}) *`}
                            value={startTime}
                            onChange={(value) => setStartTime(value)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: startTimeError.length > 0,
                                    helperText: startTimeError,
                                    inputProps: { "data-testid": "startTime" }
                                }
                            }}
                        />
                        <DateTimePicker
                            label={`End Time (${GetTimezone()}) *`}
                            value={endTime}
                            onChange={(value) => setEndTime(value)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: endTimeError.length > 0,
                                    helperText: endTimeError,
                                    inputProps: { "data-testid": "endTime" }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} sx={{ mr: "auto" }}>
                    Close
                </Button>
                <Button onClick={handleSubmit}>Submit</Button>
            </DialogActions>
        </Dialog>
    );
}
